/**
 * 会话状态管理类型
 * @author sunzhiguang
 * @date 2020/6/18
 */

import { MessageCategoryType, MessageNotifyType } from "@/types/chat/enum";
import { UserInfo } from "@/models/user";

import { ChatMessage, Employee, MessageStatus, Session, UID } from "@/types/chat";
import GroupRoster, { GroupRosterIdentity } from "@jd/jdee.im.sdk/lib/es/model/Roster";
import ConnectState from "@jd/jdee.im.sdk/lib/es/enum/ConnectState";
import Task from "@jd/jdee-fs-desktop/lib/es/Task";

export interface NotifyTypeConfig {
  enable: boolean;
  count: number; // 数量
  normalIcon: string; // 未选中icon
  selectedIcon: string; // 选中icon
  title: string; // 显示标题
  clstag: string;
}

export interface NotifyTypeMap {
  [key: string]: NotifyTypeConfig;
}

export interface CategoryConfig {
  enable: boolean;
  normalIcon: string; // 未选中icon
  selectedIcon: string; // 选中icon
  title: string; // 显示标题
  clstag: string;
}

export interface CategoryMap {
  [key: string]: CategoryConfig;
}

export interface SessionStatusInfo {
  status: MessageStatus;
  timestamp: number;
  info: {
    atUsers: UID[];
    content: string;
    atContent?: string;
    delta: any;
  };
}

export interface MessageUploadStateMap {
  [key: string]: {
    message: ChatMessage;
    task: Task;
    progress: number;
    uploadResult: any;
  };
}

export type SessionFork = Session & {
  unnreadCountPseudo?: number;
  unreadCountFork?: number;
  lastReadMidFork?: number;
  lastReadMsg?: any;
  lastReadIdFork?: string;
};

/**
 * 会话id - 消息 映射关系
 */
export interface SessionStatusMap {
  [key: string]: SessionStatusInfo;
}

/**
 * 会话id - 消息 映射关系
 */
export interface SessionMessageMap {
  [key: string]: ChatMessage[];
}

/**
 * 临时会话id - 消息 映射关系
 */
export interface TempSessionMessageMap {
  [key: string]: ChatMessage[];
}

/**
 * 会话链接状态
 */
export interface ChatConnectState {
  state: ConnectState; // 当前状态
  reason?: any; // 状态内容
}

export interface AITabState extends Session {
  pin: string;
}

export interface QuickReply {
  detail: string;
  quickReplyID: string;
  orderId: number;
  qrType: number; // 0:个人定义 1:系统定义
}

export interface VoiceMessageMap {
  mid: number;
  text: string;
}

export interface SlitaireInfo {
  sessionId: string;
  info: {
    title: string;
    desc: string;
  };
}

export interface SlitaireMap {
  [key: string]: SlitaireInfo;
}

export enum LLMMessageStatus {
  START = "start",
  LOADING = "loading",
  PENDING = "pending",
  END = "end",
  STOP = "stop"
}

export default interface ChatState {
  messages: any[];
  type: string;
  selectMore: boolean;
  topCount: number;
  unReadCount: number;
  atCount: number;
  opCount: number;
  connectState: ChatConnectState;
  // 消息提醒类型：全部消息 未读消息 重要消息
  notifyTypeTitle: string; // 当前显示选中消息提醒的标题文本内容
  notifyType: NotifyTypeMap; // 消息提醒配置
  selectedNotifyType: MessageNotifyType; // 当前选中消息提醒类型

  // 消息分类： 时间分类，类型分类
  categoryTitle: string; // 当前显示选中消息分类的标题文本内容
  category: CategoryMap; // 消息分类配置
  selectedCategory: MessageCategoryType; // 当前选中消息分类

  allSessionList: Session[];
  topUpSessionList: Session[]; // 置顶会话列表
  topUpModeCustom: string; // 置顶布局模式，默认水平布局
  topUpExpanded: boolean; // 置顶区域展开状态
  editorMode: string; // 输入框模式
  sessionPage: number; // 会话加载页
  sessionLoadEnd: boolean;
  aiNoticeSessionList: Session[];
  // 当前登录用户的信息
  currentEmployee: Employee;

  sessionMembers: Partial<Employee>[];

  // 群成员信息
  groupRosters: Partial<GroupRoster>[];

  // 群机器人信息
  groupRobots: Partial<any>[];

  // 群主信息
  administrator: Partial<GroupRoster>[];
  // 群管理员信息
  sub_administrator: Partial<GroupRoster>[];
  // 群普通成员信息
  ordinary: Partial<GroupRoster>[];
  groupMemberSessionId: string;
  groupRosterIdentity: GroupRosterIdentity | "";

  // 当前选中的会话ID
  selectedSession: SessionFork;
  preSelectedSessionId: string;
  sessionStatusInfo: SessionStatusInfo;

  messageUploadStateMap: MessageUploadStateMap;

  firstChatMessage: ChatMessage;
  lastChatMessage: ChatMessage;

  // 消息状态map
  sessionStatusMap: SessionStatusMap;

  // 输入焦点状态
  messageInputFocus: boolean;

  // 当前窗口会话消息
  sessionMessageMap: SessionMessageMap;
  sessionMessages: ChatMessage[];
  sessionMessagesLength: number;
  prevAISessionMessages: ChatMessage[];
  // 临时消息，如上传的文件，视频，图片等；
  tempSessionMessageMap: TempSessionMessageMap;
  tempSessionMessages: ChatMessage[];

  currentOperateMessageId: string;

  topSessionList: Session[];

  allTopCount: number;
  loadAllSessionListEnd: boolean;
  globalDrawerVisible: boolean;
  messageDrawerVisible: boolean;
  appcationApplyMessageId: string;
  imageMessages: any[];
  mainWinShow: boolean;
  identityInfoModal?: boolean;
  selectedAITab: AITabState;
  filterTabList: any[];
  sessionScrollIndex: number;
  sessionUnReadList: any[];
  isDragging: boolean;
  captureType: string;
  sendMsgType: string;
  sMid: number;
  sText: string;
  sTimestamp: number;
  // 定位前消息
  skipAnchorId: Record<string, any>;
  NoticeDetailsVisible: boolean;
  notificationId: string;
  SolitaireDetailsVisible: boolean;
  solitaireId: any;
  NoticeHisVisible: boolean;
  NoticeDetailsHisId: any;
  SolitaireHisVisible: boolean;
  solitaireSessionId: any;
  updateSessionMessagesTimestamp: number;
  isMessageModule: boolean;
  dbVersion: string;
  migrateVisiable: false;
  // 常用语
  quickReplyVersion: number;
  quickReplys: QuickReply[];
  downloadingMids: string[];
  saveAsCount: number;
  voiceMessageMap: VoiceMessageMap[];
  slitaireMap: SlitaireMap;
  questionHistory: any[];
  privateUser: any;
  isSelectedColor: string;
  showTagList: any;
  lastUnreadMessage: any;
  showSyncMsgTip: boolean;
  customAppList: Session[];
  noticeInited: boolean;
  // 跨租户消息
  otherTeamNotifyList: Record<string, number>;
  // 反馈标签列表
  llmTagsList: Record<string, any[]>;
  llmSetting: Record<string, any>;
  llmAgentList: any[];
  llmInfo: {
    files: any[];
    images?: any[];
    isReady?: boolean;
  } | null;
  llmQuestionInfo: {
    questionId?: string;
    status?: LLMMessageStatus;
  };
  currentAIRobot: any;
};

export interface UserState {
  userInfo: UserInfo;
  userData: any;
}

// 外部联系人申请接口
export interface ApprExternalContacts {
  apprId: string; // 审核ID不能为空
  apprStatus: number; // 审核状态不能为空 1-审核成功 2-审核失败
  apprOpinion: string; // 审核意见最大支持100个长度
}
// 团队申请审批接口
export interface ApprContacts {
  lang: string; // 固定
  approveId: string; // 审批记录id
  approveStatus: number; // 2: 通过, 3:驳回
  rejectReason: string; // 驳回原因
  resourceId: string; // 固定
}
// 外部联系人申请加入京办列表
export interface PageExternalContactsApprInfoReqParam {
  appId: string; // 应用ID
  teamId: string; // 团队ID
  inviterUserId: string; // 邀请人用户ID
  page: number; // 页数
  limit: number; // 每页数量
}
export interface EmplApproveList {
  pageNo: number;
  pageSize: number;
  emplSourceList: number[]; // 固定值
  resourceId: string; // 固定值
}
// 身份信息换取北京通号接口
export interface BindBeijingTongAccount {
  name: string; // 姓名
  idCard: string; // 身份证号
  mobile: string; // 手机号
}
export interface ICustomAppData {
  app: string;
  appKey: string;
  appName: string;
  pin: string;
  teamId: string;
  sessionId: string;
  appId: string;
  avatar?: string;
}

export interface IALLCustomAppData {
  globalConfigList: ICustomAppData[];
  userConfigList: ICustomAppData[];
}
